import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Checkbox,
  RadioGroup,
  Radio,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { sampleService, sampleErrorMessage } from '../../Services/SampleService';
import { formatLocalTime } from '../../Utils/dateUtils';
import RejectSampleDialog from './RejectSampleDialog';
import { SampleStatusChip, sourceLabel } from './sampleUi';
import './Samples.css';

const CONDITIONS_FALLBACK = ['Good'];

// <input type="datetime-local"> works in local time; the API stores what the
// rest of the app treats as UTC.
const toInputDateTime = (value) => (value ? formatLocalTime(value, 'YYYY-MM-DDTHH:mm') : '');
const fromInputDateTime = (value) => (value ? new Date(value).toISOString() : null);

const emptyForm = (tests) => ({
  id: null,
  sampleTypeId: '',
  source: 'InHouse',
  externalSource: '',
  containerType: '',
  volume: '',
  condition: 'Good',
  status: '',
  collectedAt: '',
  collectedBy: '',
  notes: '',
  // Pre-tick every test that has no sample yet.
  labOrderIds: tests.filter((t) => !t.sampleId).map((t) => t.labOrderId)
});

// Per-order sample collection: shows the order's tests (and which sample
// covers each), its samples, and a form to add/edit one.
export default function SampleCollectionDialog({ orderId, onClose, onChanged }) {
  const open = !!orderId;
  const [data, setData] = useState(null);
  const [types, setTypes] = useState([]);
  const [conditions, setConditions] = useState(CONDITIONS_FALLBACK);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null); // null = form hidden
  const [submitting, setSubmitting] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [orderData, typeList, metadata] = await Promise.all([
        sampleService.getForOrder(orderId),
        sampleService.getTypes(true),
        sampleService.getMetadata()
      ]);
      setData(orderData);
      setTypes(typeList);
      setConditions(metadata?.conditions?.length ? metadata.conditions : CONDITIONS_FALLBACK);
      // Open the form straight away when nothing has been collected yet.
      setForm(orderData.samples.length === 0 ? emptyForm(orderData.tests) : null);
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to load samples for this order'));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    else {
      setData(null);
      setForm(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const refresh = async () => {
    const orderData = await sampleService.getForOrder(orderId);
    setData(orderData);
    onChanged?.();
    return orderData;
  };

  const handleChange = (e) => {
    const { name } = e.target;
    // Native <select> gives strings; the type id is a number everywhere else.
    const value = name === 'sampleTypeId' && e.target.value !== '' ? Number(e.target.value) : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Prefill the container from the type's default when picking a type.
      if (name === 'sampleTypeId') {
        const type = types.find((t) => t.id === value);
        if (type?.defaultContainer) next.containerType = type.defaultContainer;
      }
      return next;
    });
  };

  const toggleTest = (labOrderId) => {
    setForm((prev) => ({
      ...prev,
      labOrderIds: prev.labOrderIds.includes(labOrderId)
        ? prev.labOrderIds.filter((id) => id !== labOrderId)
        : [...prev.labOrderIds, labOrderId]
    }));
  };

  const startEdit = (sample) => {
    setForm({
      id: sample.id,
      sampleTypeId: sample.sampleTypeId,
      source: sample.source,
      externalSource: sample.externalSource || '',
      containerType: sample.containerType || '',
      volume: sample.volume || '',
      condition: sample.condition || 'Good',
      status: sample.status,
      collectedAt: toInputDateTime(sample.collectedAt),
      collectedBy: sample.collectedBy || '',
      notes: sample.notes || '',
      labOrderIds: sample.labOrderIds
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.labOrderIds.length === 0) {
      toast.error('Select at least one test for this sample');
      return;
    }
    const payload = {
      ...form,
      orderId,
      status: form.id ? undefined : form.status || undefined,
      collectedAt: fromInputDateTime(form.collectedAt)
    };
    setSubmitting(true);
    try {
      if (form.id) {
        await sampleService.update(form.id, payload);
        toast.success('Sample updated');
      } else {
        const created = await sampleService.create(payload);
        toast.success(`Sample ${created.sampleNo} saved`);
      }
      const orderData = await refresh();
      // Keep the form open for the next sample if some tests are still uncovered.
      const remaining = orderData.tests.filter((t) => !t.sampleId);
      setForm(!form.id && remaining.length > 0 ? emptyForm(orderData.tests) : null);
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to save sample'));
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (sample, status, reason) => {
    try {
      await sampleService.setStatus(sample.id, status, reason);
      toast.success(`Sample ${sample.sampleNo} marked ${status.toLowerCase()}`);
      await refresh();
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to update sample status'));
    }
  };

  const handleDelete = async (sample) => {
    if (!window.confirm(`Delete sample ${sample.sampleNo}? Use this only for samples entered by mistake.`)) return;
    try {
      await sampleService.remove(sample.id);
      toast.success('Sample deleted');
      await refresh();
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to delete sample'));
    }
  };

  const canPickTest = (test) => !test.sampleId || test.sampleId === form?.id;
  const activeTypes = types.filter((t) => t.isActive || t.id === form?.sampleTypeId);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" className="common-modal">
        <DialogTitle className="d-flex justify-content-between align-items-center">
          <span>
            Samples — Order #{orderId}
            {data?.patientName && <span className="common-Labels ms-2">{data.patientName.toUpperCase()}</span>}
          </span>
          <IconButton onClick={onClose} className="common-cross-button">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="common-FontFamily">
          {loading || !data ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="row">
              <div className="col-md-5">
                <label className="common-Labels d-block mb-1">Tests in this order</label>
                <div className="sample-test-list mb-3">
                  {data.tests.length === 0 && <div className="text-muted py-2">This order has no tests.</div>}
                  {data.tests.map((test) => (
                    <div key={test.labOrderId} className="sample-test-item">
                      {test.testName || `Test #${test.labOrderId}`}
                      <span className="sample-test-owner">
                        {test.sampleNo || <span className="sample-status sample-status-pending">Pending</span>}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="common-Labels m-0">Samples</label>
                  {!form && (
                    <Button size="small" variant="contained" className="common-button" startIcon={<AddIcon />} onClick={() => setForm(emptyForm(data.tests))}>
                      Add sample
                    </Button>
                  )}
                </div>
                {data.samples.length === 0 && <div className="text-muted common-FontSize-small">No samples yet.</div>}
                {data.samples.map((sample) => (
                  <div key={sample.id} className="sample-card">
                    <div className="sample-card-header">
                      <strong>{sample.sampleNo}</strong>
                      <SampleStatusChip status={sample.status} />
                    </div>
                    <div className="sample-card-meta">
                      {sample.sampleTypeName}{sample.containerType ? ` · ${sample.containerType}` : ''}{sample.volume ? ` · ${sample.volume}` : ''}
                      <br />
                      <span className="sample-source">{sourceLabel(sample)}</span>
                      {sample.condition && sample.condition !== 'Good' && <> · <span className="text-danger">{sample.condition}</span></>}
                      <br />
                      Tests: {sample.testNames.join(', ')}
                      {sample.collectedAt && <><br />Collected {formatLocalTime(sample.collectedAt)}{sample.collectedBy ? ` by ${sample.collectedBy}` : ''}</>}
                      {sample.receivedAt && <><br />Received {formatLocalTime(sample.receivedAt)}{sample.receivedBy ? ` by ${sample.receivedBy}` : ''}</>}
                      {sample.status === 'Rejected' && <><br /><span className="text-danger">Rejected: {sample.rejectionReason}</span></>}
                      {sample.notes && <><br />Notes: {sample.notes}</>}
                    </div>
                    <div className="sample-card-actions mt-1">
                      {sample.status === 'Collected' && (
                        <Button size="small" variant="outlined" color="success" onClick={() => setStatus(sample, 'Received')}>Receive</Button>
                      )}
                      {sample.status !== 'Rejected' ? (
                        <Button size="small" variant="outlined" color="error" onClick={() => setRejectTarget(sample)}>Reject</Button>
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => setStatus(sample, sample.receivedAt ? 'Received' : 'Collected')}>Undo reject</Button>
                      )}
                      <IconButton size="small" onClick={() => startEdit(sample)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(sample)} title="Delete">
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>

              <div className="col-md-7">
                {form ? (
                  <form onSubmit={handleSubmit}>
                    <label className="common-Labels d-block mb-2">{form.id ? 'Edit sample' : 'New sample'}</label>

                    <div className="mb-2">
                      <label className="common-Labels d-block">Sample source <span className="common-Asterisk">*</span></label>
                      <RadioGroup row name="source" value={form.source} onChange={handleChange}>
                        <FormControlLabel value="InHouse" control={<Radio size="small" />} label="Collected in lab" />
                        <FormControlLabel value="External" control={<Radio size="small" />} label="Brought from outside" />
                      </RadioGroup>
                    </div>

                    {form.source === 'External' && (
                      <div className="mb-2">
                        <label className="common-Labels d-block">Brought from <span className="common-Asterisk">*</span></label>
                        <input
                          className="form-control common-Input"
                          name="externalSource"
                          value={form.externalSource}
                          onChange={handleChange}
                          placeholder="Hospital / clinic / collection center / home collection"
                          required
                        />
                      </div>
                    )}

                    <div className="row">
                      <div className="col-sm-6 mb-2">
                        <label className="common-Labels d-block">Sample type <span className="common-Asterisk">*</span></label>
                        <select className="form-select common-Input" name="sampleTypeId" value={form.sampleTypeId} onChange={handleChange} required>
                          <option value="" disabled>Select type</option>
                          {activeTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6 mb-2">
                        <label className="common-Labels d-block">Container</label>
                        <input className="form-control common-Input" name="containerType" value={form.containerType} onChange={handleChange} placeholder="e.g. EDTA tube" />
                      </div>
                      <div className="col-sm-6 mb-2">
                        <label className="common-Labels d-block">Volume</label>
                        <input className="form-control common-Input" name="volume" value={form.volume} onChange={handleChange} placeholder="e.g. 3 ml" />
                      </div>
                      <div className="col-sm-6 mb-2">
                        <label className="common-Labels d-block">Condition</label>
                        <select className="form-select common-Input" name="condition" value={form.condition} onChange={handleChange}>
                          {conditions.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6 mb-2">
                        <label className="common-Labels d-block">Collected at</label>
                        <input
                          className="form-control common-Input"
                          type="datetime-local"
                          name="collectedAt"
                          value={form.collectedAt}
                          onChange={handleChange}
                        />
                        {form.source === 'InHouse' && !form.id && (
                          <div className="common-Font-10 text-muted">Leave empty to use the current time</div>
                        )}
                      </div>
                      <div className="col-sm-6 mb-2">
                        <label className="common-Labels d-block">Collected by</label>
                        <input
                          className="form-control common-Input"
                          name="collectedBy"
                          value={form.collectedBy}
                          onChange={handleChange}
                          placeholder={form.source === 'InHouse' ? 'Defaults to you' : 'Name, if known'}
                        />
                      </div>
                      {!form.id && (
                        <div className="col-sm-6 mb-2">
                          <label className="common-Labels d-block">Status</label>
                          <select className="form-select common-Input" name="status" value={form.status} onChange={handleChange}>
                            <option value="">
                              {form.source === 'InHouse' ? 'Received (default for in-lab)' : 'Collected — not yet received (default)'}
                            </option>
                            <option value="Collected">Collected — not yet received</option>
                            <option value="Received">Received at lab</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="mb-2">
                      <label className="common-Labels d-block">Tests on this sample <span className="common-Asterisk">*</span></label>
                      <div className="sample-test-list">
                        {data.tests.map((test) => (
                          <label key={test.labOrderId} className="sample-test-item" style={{ opacity: canPickTest(test) ? 1 : 0.5 }}>
                            <Checkbox
                              size="small"
                              sx={{ p: '2px' }}
                              checked={form.labOrderIds.includes(test.labOrderId)}
                              disabled={!canPickTest(test)}
                              onChange={() => toggleTest(test.labOrderId)}
                            />
                            {test.testName || `Test #${test.labOrderId}`}
                            {!canPickTest(test) && <span className="sample-test-owner">on {test.sampleNo}</span>}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="mb-2">
                      <label className="common-Labels d-block">Notes</label>
                      <textarea className="form-control common-Input" name="notes" rows={2} value={form.notes} onChange={handleChange} />
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <Button onClick={() => setForm(null)} disabled={submitting}>Cancel</Button>
                      <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? 'Saving...' : form.id ? 'Update sample' : 'Save sample'}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-muted common-FontSize-small text-center py-5">
                    {data.tests.some((t) => !t.sampleId)
                      ? 'Some tests have no sample yet. Click "Add sample" to collect one.'
                      : 'All tests in this order have a sample.'}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <RejectSampleDialog
        sample={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={async (reason) => {
          await setStatus(rejectTarget, 'Rejected', reason);
          setRejectTarget(null);
        }}
      />
    </>
  );
}
